// THE PO'S OWN HEADER, ABOVE THE LINES, EDITABLE.
//
// ⚠️ WHAT THIS PANEL SHOWS IS WHAT THE CUSTOMER'S DOCUMENT PRINTS, and every
// field is editable because the reader can be wrong and a person can see the
// paper. Nothing here is derived, computed or defaulted: a heading the
// document printed with nothing under it arrives EMPTY and stays empty until
// somebody types something, because empty is the honest reading of that.
//
// ⚠️ NONE OF THIS TOUCHES THE CUSTOMER MASTER. `customer_name` is the buying
// entity the PO names, as text. It is not the quote's customer, it is not a
// res.partner link, and neither saving nor editing it creates, renames or
// contacts anybody -- see `_PO_HEADER_FIELDS` on the backend and
// `test_po_header.py`, which tests that rule in both directions. When it
// differs from the quote's customer, the warning strip below says so and
// stops there.
//
// ⚠️ THE WARNINGS ARE AMBER AND NEVER BLOCK. A difference between the
// customer's paperwork and our quote is a fact for a person to judge, not an
// error for a machine to refuse. The decisions live in
// `services/poWarnings.ts`; this file renders what it is handed and decides
// nothing, the same split as the comparison panel and po_comparison.py.

import {
  colors, semanticColors, radius, spacing, heights, typography,
} from "@/styles/tokens";
import type { PoWarning } from "@/services/poWarnings";

/** The header as the form edits it: every value a string, so an input can
 *  hold it and an empty box is distinguishable from a zero.
 *
 *  ⚠️ STRINGS EVEN FOR THE TOTAL. `po_total` is a Float on the record, and
 *  storing it here as a number would make "" indistinguishable from 0 -- the
 *  same blank-is-not-zero defect the line quantity check already carries. */
export interface PoHeaderDraft {
  po_format: string;
  po_revision: string;
  po_revision_date: string;
  vendor_name: string;
  customer_name: string;
  customer_code: string;
  payment_terms: string;
  freight_terms: string;
  fob: string;
  shipping_method: string;
  po_total: string;
  po_total_text: string;
  contact_emails: string;
  contact_phones: string;
  extraction_flags: string;
}

export const EMPTY_PO_HEADER: PoHeaderDraft = {
  po_format: "", po_revision: "", po_revision_date: "",
  vendor_name: "", customer_name: "", customer_code: "",
  payment_terms: "", freight_terms: "", fob: "", shipping_method: "",
  po_total: "", po_total_text: "",
  contact_emails: "", contact_phones: "", extraction_flags: "",
};

/** Which fields get a box, in the order they are printed on the document.
 *
 *  ⚠️ `po_format`, `po_total_text` AND `extraction_flags` ARE NOT HERE, and
 *  that is deliberate rather than an omission. They are CARRIED on the draft
 *  and saved, but they are not things a person edits: the format is which
 *  reader produced the record, the printed-total text is the document's own
 *  typography (editing it would make it no longer "as printed"), and the
 *  flags are what the reader could not settle -- a note about the read, not a
 *  fact about the order. */
const FIELDS: { key: keyof PoHeaderDraft; label: string; wide?: boolean }[] = [
  { key: "customer_name", label: "Customer Name (on the PO)", wide: true },
  { key: "customer_code", label: "Customer Code" },
  { key: "vendor_name", label: "Vendor (on the PO)", wide: true },
  { key: "po_revision", label: "PO Revision" },
  { key: "po_revision_date", label: "Revision Date" },
  { key: "payment_terms", label: "Payment Terms" },
  { key: "freight_terms", label: "Freight Terms" },
  { key: "fob", label: "FOB" },
  { key: "shipping_method", label: "Shipping Method" },
  { key: "po_total", label: "PO Total" },
  { key: "contact_emails", label: "E-mail(s) on the PO", wide: true },
  { key: "contact_phones", label: "Phone(s) on the PO", wide: true },
];

/** The two fields that hold SEVERAL labelled values, one "label: value" per
 *  line -- the shape the record stores and `headerFromExtracted` builds. */
const CONTACT_KEYS: (keyof PoHeaderDraft)[] = ["contact_emails", "contact_phones"];

export interface PoContact {
  /** The label the PO printed, exactly; null when none was recorded. */
  label: string | null;
  value: string;
}

/** One entry per stored line.
 *
 *  ⚠️ SPLIT AT THE FIRST ": ", NEVER THE LAST. A label never contains one (the
 *  reader strips the trailing colon), and an address or a phone never does
 *  either, so the first is the boundary. Splitting at the last would turn a
 *  line that already holds two values into one plausible-looking label and
 *  hide the first value inside it; split at the first, the oddity stays in the
 *  value box where a person can see it. */
export function parseContacts(text: string): PoContact[] {
  return text.split(/\r?\n/).filter((l) => l.trim() !== "").map((line) => {
    const at = line.indexOf(": ");
    return at > 0
      ? { label: line.slice(0, at).trim(), value: line.slice(at + 2).trim() }
      : { label: null, value: line.trim() };
  });
}

export function serialiseContacts(list: PoContact[]): string {
  return list.map((c) => (c.label ? `${c.label}: ${c.value}` : c.value)).join("\n");
}

const INPUT: React.CSSProperties = {
  height: heights.input, padding: `0 ${spacing[8]}px`,
  fontSize: typography.fontSize.sm, color: semanticColors.textPrimary,
  backgroundColor: semanticColors.surface,
  border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
  outline: "none", minWidth: 0, width: "100%", boxSizing: "border-box",
};

const AMBER_BOX: React.CSSProperties = {
  padding: `${spacing[8]}px ${spacing[10]}px`,
  backgroundColor: colors.amber50,
  border: `1px solid ${colors.amber200}`,
  borderRadius: radius.base,
  fontSize: typography.fontSize.sm,
  color: semanticColors.textPrimary,
  marginBottom: spacing[6],
};

/** The amber strip. One box per warning, each naming its own difference.
 *
 *  ⚠️ ONE BOX PER WARNING, NOT ONE BOX LISTING THEM. Four differences folded
 *  into a single paragraph read as one vague problem; separately they read as
 *  four checkable facts, and `data-warning` lets each be asserted on its own.
 *  ⚠️ AND NOT ONE OF THEM DISABLES ANYTHING. There is no button on this
 *  component. */
export function PoWarningStrip({ warnings, testId = "po-header-warnings" }: {
  warnings: PoWarning[];
  testId?: string;
}) {
  if (!warnings.length) return null;
  return (
    <div data-testid={testId}>
      {warnings.map((w) => (
        <div key={w.code} data-testid={`po-warning-${w.code}`}
             data-warning={w.code} style={AMBER_BOX}>
          {w.message}
        </div>
      ))}
    </div>
  );
}

export function PoHeaderFields({
  header, onChange, warnings, testIdPrefix = "po-header", readOnly = false,
  poDate = null, currency = null,
}: {
  header: PoHeaderDraft;
  onChange: (key: keyof PoHeaderDraft, value: string) => void;
  warnings: PoWarning[];
  testIdPrefix?: string;
  readOnly?: boolean;
  /** ⚠️ SHOWN, NOT EDITABLE. The PO's own date and currency are read off the
   *  document and ride the save, but neither is a free-text field: a date has
   *  to parse and a currency has to match a res.currency exactly. Displayed so
   *  a person checking against the paper can see what was recorded. */
  poDate?: string | null;
  currency?: string | null;
}) {
  return (
    <div
      data-testid={`${testIdPrefix}-fields`}
      data-po-format={header.po_format || undefined}
      style={{
        marginBottom: spacing[10], padding: spacing[10],
        backgroundColor: semanticColors.surface,
        border: `1px solid ${semanticColors.border}`,
        borderRadius: radius.base,
      }}
    >
      <div style={{
        fontSize: typography.fontSize.sm,
        fontWeight: typography.fontWeight.medium,
        color: semanticColors.textPrimary, marginBottom: spacing[2],
      }}>
        What the PO says
      </div>
      {/* ⚠️ SAID OUT LOUD. Without this sentence an editable "Customer Name"
          box beside a quote reads as the quote's customer, and correcting a
          misread would look like it fixed the quote. It does not: this is the
          document's own text and nothing here writes to a customer record. */}
      <div style={{
        fontSize: typography.fontSize.xs, color: semanticColors.textMuted,
        marginBottom: spacing[8],
      }}>
        Read from the document and editable here. These are the PO's own
        details — none of them changes this quote's customer or creates one.
      </div>

      <PoWarningStrip warnings={warnings} testId={`${testIdPrefix}-warnings`} />

      {/* ⚠️ WHAT THE READER COULD NOT SETTLE, SHOWN RATHER THAN SWALLOWED. An
          ambiguous date is left EMPTY on purpose; without this note the empty
          box looks like a document that printed nothing. */}
      {header.extraction_flags && (
        <div data-testid={`${testIdPrefix}-flags`} style={AMBER_BOX}>
          {header.extraction_flags}
        </div>
      )}

      <div style={{
        display: "grid", gridTemplateColumns: "1fr 1fr 1fr",
        gap: spacing[8], alignItems: "end",
      }}>
        {FIELDS.map(({ key, label, wide }) => CONTACT_KEYS.includes(key) ? (
          // ⚠️ ONE BLOCK PER LABELLED VALUE, NEVER ONE BOX FOR ALL OF THEM.
          // These were "label: value" lines in a single <input>, and an input
          // STRIPS LINE BREAKS from its value: the real Oracle PO's two
          // addresses showed as "E-mail: …comDeliver To Contact: …", two
          // printed fields joined end to end, and an edit to that box would
          // have saved them that way. Each value now has its own box under
          // the PO's own label, with the label kept out of the value. No
          // value recorded -> the one app-labelled "Not printed" box, as
          // before.
          <div key={key} data-testid={`${testIdPrefix}-${key}`}
               style={{ display: "contents" }}>
            {(parseContacts(header[key]).length
              ? parseContacts(header[key])
              : [{ label: null, value: "" }]
            ).map((c, i, list) => (
              <label key={i} data-testid={`${testIdPrefix}-${key}-${i}`}
                     data-label={c.label ?? undefined}
                     style={{ display: "block", minWidth: 0, gridColumn: "span 2" }}>
                <span style={{
                  display: "block", fontSize: typography.fontSize.xs,
                  color: semanticColors.textSecondary, marginBottom: spacing[2],
                }}>
                  {c.label ?? label}
                </span>
                <input
                  data-testid={`${testIdPrefix}-${key}-${i}-value`}
                  value={c.value}
                  onChange={(e) => onChange(key, serialiseContacts(
                    list.map((o, j) => (j === i ? { ...o, value: e.target.value } : o))))}
                  readOnly={readOnly}
                  placeholder="Not printed"
                  style={INPUT}
                />
              </label>
            ))}
          </div>
        ) : (
          <label key={key} style={{
            display: "block", minWidth: 0,
            gridColumn: wide ? "span 2" : undefined,
          }}>
            <span style={{
              display: "block", fontSize: typography.fontSize.xs,
              color: semanticColors.textSecondary, marginBottom: spacing[2],
            }}>
              {label}
            </span>
            <input
              data-testid={`${testIdPrefix}-${key}`}
              value={header[key]}
              onChange={(e) => onChange(key, e.target.value)}
              readOnly={readOnly}
              // ⚠️ THE PLACEHOLDER IS "Not printed", NEVER A GUESS AT WHAT
              // BELONGS THERE. An empty box on this panel means the document
              // printed no value under that heading, which is a reading.
              placeholder="Not printed"
              style={INPUT}
            />
          </label>
        ))}
      </div>

      {/* The PO's own date and currency, as read. Not editable -- see the
          props' note. Absent shows nothing rather than an em-dash, so an
          unrecorded value is not mistaken for a recorded blank. */}
      {(poDate || currency) && (
        <div data-testid={`${testIdPrefix}-meta`} style={{
          marginTop: spacing[6], fontSize: typography.fontSize.xs,
          color: semanticColors.textMuted,
        }}>
          {poDate ? `PO date ${poDate}` : ""}
          {poDate && currency ? " · " : ""}
          {currency ? `priced in ${currency}` : ""}
        </div>
      )}

      {/* The document's own total, as printed. Shown BESIDE the parsed figure
          rather than in place of it: the number is what arithmetic uses and
          the text is what a person checks against the paper. */}
      {header.po_total_text && header.po_total_text !== header.po_total && (
        <div data-testid={`${testIdPrefix}-total-as-printed`} style={{
          marginTop: spacing[6], fontSize: typography.fontSize.xs,
          color: semanticColors.textMuted,
        }}>
          Total as printed: {header.po_total_text}
        </div>
      )}
    </div>
  );
}
